/**
 * Document-chase agent — the fifth producer that does *real* work, not simulation.
 *
 * The system scanner already *diagnoses* documents stuck in the client's court
 * (added in the 2026-06-22 doc-scanner pass):
 *   - "Lapsed document" — a sent/viewed proposal or contract past its `validUntil`,
 *     still counted as open pipeline (UX_REVIEW move #4: a lapsed-but-unclosed doc
 *     silently inflates ContractsView's open-pipeline figure until someone acts).
 *   - "Awaiting client" — out the door but no movement in ≥7 days, going cold.
 *
 * This producer *treats* them (UX_REVIEW move #4: "the scanner diagnoses; it should
 * treat"). It reads the *real* documents collection, takes those two finding
 * categories, and proposes a *real* `create_task` write for each into the approvals
 * inbox — a chase task tied to the document's own account / contact so it lands on
 * that entity's page. A lapsed doc gets an *urgent* "re-issue or close" task; an
 * awaiting doc gets a *high* "nudge the client" task.
 *
 * It performs no write itself — it only proposes. The operator approves in the
 * Dashboard approvals inbox, and `resolveApproval` runs the action through
 * `runActionWithUndo`, which genuinely creates the task (tied to the entity so it
 * shows on their page) and is undoable. So the loop is honest end-to-end:
 *
 *   real data  →  real proposal  →  operator approval  →  real store write
 *
 * Domains are split on purpose so the producers never double up: the revenue agent
 * owns billing/renewal tasks (titles "Collect / Confirm / Start renewal — …"), this
 * owns document-lifecycle tasks (titles "Re-issue or close … / Nudge client on …").
 * Distinct title namespaces → the shared `create_task` de-dupe never collides them.
 * No network, no API key — same db adapter the humans use, so it works on
 * localStorage today and Firestore the moment the flag flips.
 */

import { scanSystem } from './systemScanner';
import { queueApproval, listApprovals } from './approvals';
import type { BusinessDoc } from '../db/types';
import type { CreateTaskAction } from './types';

/** Scanner finding categories this agent knows how to turn into a chase task. */
const ACTIONABLE_CATEGORIES = new Set(['Lapsed document', 'Awaiting client']);

export interface ProposeDocumentChasesResult {
  /** How many new chase tasks were queued for approval. */
  proposed: number;
  /** Findings that already had a matching pending task (skipped). */
  skipped: number;
}

/**
 * Turn one document finding into a concrete, executable `create_task` proposal.
 *
 * The task is linked to the document's *own* entity — `accountId`/`accountName`
 * for an account-attached doc, `personId`/`personName` for a person-attached one —
 * so the chase task surfaces on the same page as the deal it concerns (the scanner
 * finding only carries the *document* id, so the link must be read off the doc).
 *
 * `value` carries the document's deal amount: the open-pipeline dollars this chase
 * is trying to either recover (re-issue/close a lapsed doc) or unstick (nudge an
 * awaiting one). Influenced / at-risk, never banked — mirrors the revenue agent's
 * semantics so the value ledger credits the dollars an approved action *touched*,
 * not collected. Omitted (left `undefined`) when the doc carries no value, so the
 * ledger never invents a number.
 *
 * Exported for unit testing the pure mapping without touching the store.
 */
export function chaseTaskFor(doc: BusinessDoc, category: string): CreateTaskAction | null {
  if (!ACTIONABLE_CATEGORIES.has(category)) return null;

  const noun = doc.kind === 'contract' ? 'contract' : 'proposal';
  const lapsed = category === 'Lapsed document';

  // Link to whichever entity the document hangs off. BusinessDoc denormalizes
  // entityType/entityId/entityName, so we route the task to the right collection.
  const link =
    doc.entityType === 'account'
      ? { accountId: doc.entityId, accountName: doc.entityName }
      : { personId: doc.entityId, personName: doc.entityName };

  return {
    kind: 'create_task',
    ...link,
    title: `${lapsed ? 'Re-issue or close' : 'Nudge client on'} ${noun} — ${doc.entityName} · ${doc.title}`,
    priority: lapsed ? 'urgent' : 'high',
    // No natural due date on a chase (like task-router assignments) — the operator
    // sets one when they pick it up. Left null rather than inventing a deadline.
    dueDate: null,
    // The deal dollars this chase concerns; omitted when the doc has no value.
    value: doc.value,
  };
}

/**
 * Scan `documents` and queue a `create_task` approval for each lapsed / awaiting
 * document that needs a human nudge. De-dupes by task title against pending
 * approvals so re-running the agent doesn't pile duplicate tasks for the same
 * document. Returns how many were proposed vs. skipped.
 *
 * Mirrors `proposeRevenueTasks` exactly (same scan → filter → map → de-dupe →
 * queue shape) so the producer family stays consistent and reviewable.
 */
export async function proposeDocumentChases(
  documents: BusinessDoc[],
  now = Date.now(),
): Promise<ProposeDocumentChasesResult> {
  // Only the documents sweep is consulted here, so the other collections are
  // empty — keeps the scan focused on document findings.
  const { findings } = scanSystem({
    tickets: [],
    tasks: [],
    people: [],
    accounts: [],
    documents,
    now,
  });
  const byDocId = new Map(documents.map((d) => [d.id, d]));
  const docFindings = findings.filter(
    (f) => f.source === 'document' && ACTIONABLE_CATEGORIES.has(f.category),
  );

  // Don't propose a second task for a document that already has one waiting in
  // the inbox. Titles are deterministic per (entity, document), so matching on
  // title is a stable de-dupe key — shared with the revenue agent's create_task
  // pool, but the title namespaces don't overlap.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'create_task')
      .map((a) => (a.action as CreateTaskAction).title),
  );

  let proposed = 0;
  let skipped = 0;
  for (const f of docFindings) {
    const doc = byDocId.get(f.entityId);
    if (!doc) {
      // Defensive — the scanner only emits document findings for real docs.
      skipped++;
      continue;
    }
    const action = chaseTaskFor(doc, f.category);
    if (!action || alreadyQueued.has(action.title)) {
      skipped++;
      continue;
    }
    await queueApproval(action, 'scheduler', f.detail);
    // Guard against duplicate findings producing the same task within one run.
    alreadyQueued.add(action.title);
    proposed++;
  }

  return { proposed, skipped };
}

/**
 * Narrate a document-chase run for the agent's run history. Pure so the
 * `runAgentOnce` branch stays headlessly testable — same "Ran — …" shape the
 * other producers log, so a deployed document-chaser reads consistently in the
 * Agents timeline. "Nothing to chase" when the sweep is clean; otherwise the
 * proposed / already-queued counts, never claiming a write it didn't make.
 */
export function documentChaseRunText(result: ProposeDocumentChasesResult): string {
  const { proposed, skipped } = result;
  if (proposed === 0 && skipped === 0) {
    return 'Ran — swept documents, nothing lapsed or awaiting to chase';
  }
  const bits: string[] = [];
  if (proposed > 0)
    bits.push(`proposed ${proposed} document chase${proposed === 1 ? '' : 's'} for review`);
  if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
  return `Ran — ${bits.join(' · ')}`;
}
